import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { createGlobalStyle } from 'styled-components';
import {
  kleinBlue,
  gold,
  fuchsia,
  black,
} from '@spotify-internal/encore-web-v5';
import { DEFAULT_SOCIAL_CARD } from '../../../../features/Layout/PageHead';
import { useT } from '@spotify-internal/i18n-core';

const goldHighlight = '#fbb250';
const kleinBlueActive = '#2E00AC';
const kleinBlueHighlight = '#7140F8';

const fuchsiaHighlight = 'rgb(229, 79, 169)';
const fuchsiaActive = 'rgb(154, 14, 98)';

const defaultTitle = process.env.REACT_APP_TITLE;
const defaultImage = DEFAULT_SOCIAL_CARD;

const pageHeadDefaultProps = {
  title: [] as string[],
};

// Apply gold theme
// eslint-disable-next-line no-unused-expressions
export const PageHeadStyles = createGlobalStyle`

  .nav-tabs li.active a::after,
  .no-touchevents .nav-tabs li:active a::after,
  .no-touchevents .nav-tabs li:hover a::after {
    background: ${black};
  }

  a:focus:not(.masthead a):not(.new-videos a),
  a:hover:not(.masthead a):not(.new-videos a) {
    &,
    .footer-default .nav &,
    .navbar-default .navbar-nav .open .dropdown-menu>li> & {
      color: ${goldHighlight};
    }
  }

  a.external {
    color: ${gold};

    &::after {
      background-image: url('data:image/svg+xml;utf8,<svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><title>Arrow</title><g stroke="${goldHighlight}" stroke-width="2" fill="none" fill-rule="evenodd"><path d="M1 9l8-8"/><path stroke-linejoin="round" d="M2 .998h7V8"/></g></svg>')
    }

    &:hover {
      color: ${goldHighlight};
    }
  }

  .btn-green {
    background-color: ${kleinBlue};

    &:hover {
      background-color: ${kleinBlueHighlight};
    }

    &:active,
    &:focus {
      &,
      &:hover {
        background-color: ${kleinBlueActive};
      }
    }
  }

  .btn-fuchsia {
    background-color: ${fuchsia};

    &:hover {
      background-color: ${fuchsiaHighlight};
    }

    &:active,
    &:focus {
      &,
      &:hover {
        background-color: ${fuchsiaActive};
      }
    }
  }
`;

type OwnProps = {
  title?: string[];
  description?: string;
  image?: string;
  linkedData?: {};
  children?: React.ReactNode;
};

type Props = OwnProps & typeof pageHeadDefaultProps;

export default function PageHead(props: Props) {
  const t = useT();
  const router = useRouter();

  const title = [...props.title, defaultTitle].join(' – ');
  const description =
    props.description ||
    t(
      'FRODOR_APP_DESCRIPTION',
      'Get more out of Spotify with tools & tips for artists and their teams.',
      'Application description',
    );
  const image =
    (props.image || defaultImage || '').replace(/^\/\//, 'https://') || null;
  const linkedData = props.linkedData && {
    '@context': 'http://schema.org',
    ...props.linkedData,
  };

  return (
    <Head>
      <title key="title">{title}</title>
      <meta key="meta.description" name="description" content={description} />
      <meta name="apple-itunes-app" content="app-id=1222021797" />
      <meta name="twitter:app:id:googleplay" content="com.spotify.music" />

      <meta key="meta.og:title" name="og:title" content={title} />
      <meta
        key="meta.og:description"
        name="og:description"
        content={description}
      />
      <meta
        key="meta.og:image"
        name="og:image"
        property="og:image"
        content={image as $TSFixMe}
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta key="meta.twitter:title" name="twitter:title" content={title} />
      <meta
        key="meta.twitter:image"
        name="twitter:image"
        content={image as $TSFixMe}
      />
      <meta
        key="meta.twitter:description"
        name="twitter:description"
        content={description}
      />
      <meta name="twitter:site" content="@spotifyartists" />
      <meta name="twitter:app:id:iphone" content="324684580" />
      <meta name="twitter:app:id:googleplay" content="com.spotify.music" />

      <link
        rel="alternate"
        type="application/rss+xml"
        title={`${t(
          'FRODOR_legacy-pagehead-1',
          'All Posts',
          'A collection of blog posts',
        )} – ${t('FRODOR_legacy-pagehead-2', 'News', 'News')} – ${t(
          'FRODOR_legacy-pagehead-3',
          'Spotify for Artists',
          'Spotify for Artists',
        )}`}
        href={`${router.locale ? `/${router.locale}` : ''}/blog.rss`}
        key="link.blogUrl"
      />

      {linkedData && (
        <script
          key="script.linkedData"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(linkedData) }}
        />
      )}
      {props.children}
    </Head>
  );
}

PageHead.defaultProps = pageHeadDefaultProps;
